import axios from 'axios';
import { API_LOGIN, API_REGISTER, API_TOKEN_REFRESH } from '../actions/constants';

const API_URL = process.env.REACT_APP_API_URL;

const readAuth = () => {
  try {
    return JSON.parse(localStorage.getItem('token') || '{}');
  } catch {
    return {};
  }
};

const writeAuth = (next) => {
  localStorage.setItem('token', JSON.stringify(next));
};

// Endpoints that issue/refresh tokens themselves. A 401 from these means "bad
// credentials / expired session", not "access token expired", so we must never
// attempt refresh-and-retry against them (that would loop forever).
const isAuthEndpoint = (url = '') =>
  url.includes(API_LOGIN) ||
  url.includes(API_REGISTER) ||
  url.includes(API_TOKEN_REFRESH);

const forceLogout = () => {
  localStorage.removeItem('token');
  // Hard redirect so the app + Redux auth state re-bootstrap from a clean slate.
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
};

const instance = axios.create({
  baseURL: API_URL,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
});

// Identify this client as the web SPA (so the backend issues short-lived,
// "idle window" tokens) and attach the current access token.
instance.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    config.headers['X-Client'] = 'web';

    const auth = readAuth();
    const token = auth?.access || auth?.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Single-flight refresh state: while one refresh is in progress, other callers
// queue and all resolve with the same new access token.
let isRefreshing = false;
let pendingQueue = [];

const resolveQueue = (error, token = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
};

// Performs the actual refresh round-trip and persists the (rotated) tokens.
const doRefresh = async () => {
  const auth = readAuth();
  const refresh = auth?.refresh;
  if (!refresh) {
    throw new Error('No refresh token available');
  }

  // Bare axios (not the instance) so this call doesn't recurse through these
  // interceptors.
  const { data } = await axios.post(
    `${API_URL}${API_TOKEN_REFRESH}`,
    { refresh },
    {
      headers: { 'Content-Type': 'application/json', 'X-Client': 'web' },
      withCredentials: true,
    }
  );

  const next = { ...auth, access: data.access, token: data.access };
  if (data.refresh) {
    // Rotation is enabled server-side, so a new refresh token is returned.
    next.refresh = data.refresh;
  }
  writeAuth(next);
  return data.access;
};

// Single-flight wrapper. Concurrent callers share one in-flight refresh and all
// resolve with the same new access token. Used by both the 401 interceptor and
// the idle-timer "Stay logged in" action.
export const refreshSession = () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      pendingQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  return doRefresh()
    .then((token) => {
      resolveQueue(null, token);
      return token;
    })
    .catch((error) => {
      resolveQueue(error, null);
      throw error;
    })
    .finally(() => {
      isRefreshing = false;
    });
};

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      isAuthEndpoint(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    const auth = readAuth();
    if (!auth?.refresh) {
      forceLogout();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const token = await refreshSession();
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return instance(originalRequest);
    } catch (refreshError) {
      forceLogout();
      return Promise.reject(refreshError);
    }
  }
);

export default function api() {
  return instance;
}
